import { computed, ref } from "vue";
import { defineStore } from "pinia";

import type { AdminAccountPayload } from "@stonelab/contracts";

import { AdminApiError } from "../../platform/api-client.js";
import { adminApi, clearToken, currentToken, saveToken } from "../../platform/admin-api.js";

/**
 * 登录状态。
 *
 * 令牌只存在一个地方（platform/admin-api），这里只记账号信息与过程状态。
 * 刷新页面后不信任本地缓存的账号，而是拿令牌问一次服务端（`restore`）：
 * 会话可能已经过期或被吊销，界面不该显示一个其实已经失效的登录态。
 */
export const useAuthStore = defineStore("auth", () => {
  const account = ref<AdminAccountPayload | null>(null);
  const loading = ref(false);
  const errorText = ref("");
  /** 令牌是否成功写进本地存储。写不进去时要提示用户「刷新需要重新登录」。 */
  const tokenPersisted = ref(true);

  const isAuthenticated = computed(() => account.value !== null);

  function describeError(error: unknown): string {
    if (error instanceof AdminApiError) {
      return error.message;
    }
    return "出了点问题，请重试";
  }

  async function login(username: string, password: string): Promise<boolean> {
    loading.value = true;
    errorText.value = "";
    try {
      const result = await adminApi.login(username, password);
      tokenPersisted.value = saveToken(result.token);
      account.value = result.account;
      return true;
    } catch (error) {
      errorText.value = describeError(error);
      return false;
    } finally {
      loading.value = false;
    }
  }

  async function logout(): Promise<void> {
    try {
      await adminApi.logout();
    } catch {
      // 退出失败不影响本地登出：令牌已经没用了，用户想走就该让他走。
    } finally {
      clearToken();
      account.value = null;
    }
  }

  /** 用已保存的令牌确认会话是否还有效，返回是否处于登录态。 */
  async function restore(): Promise<boolean> {
    if (currentToken() === null) {
      return false;
    }

    loading.value = true;
    try {
      account.value = await adminApi.me();
      return true;
    } catch (error) {
      // 401 已经由传输层清掉令牌；其他错误（比如服务端没起）同样当作未登录，
      // 让用户重新走一次登录流程，而不是停在一个半可信的状态上。
      if (!(error instanceof AdminApiError) || error.code !== "CLIENT_NETWORK_ERROR") {
        clearToken();
      }
      account.value = null;
      return false;
    } finally {
      loading.value = false;
    }
  }

  /** 会话失效时由传输层回调，清掉界面上的账号信息。 */
  function handleSessionExpired(): void {
    account.value = null;
  }

  return {
    account,
    loading,
    errorText,
    tokenPersisted,
    isAuthenticated,
    login,
    logout,
    restore,
    handleSessionExpired,
  };
});
